import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { enqueueTask, restartableTask } from 'ember-concurrency';
import { queryManager } from 'ember-apollo-client';
import { v4 as uuid } from 'uuid';

import sendMessageMutation from 'my-phorest/gql/mutations/send-chat-message.graphql';
import getChatMessagesQuery from 'my-phorest/gql/queries/chat-messages.graphql';
import getChatConversationsQuery from 'my-phorest/gql/queries/chat-conversations.graphql';

export default class TwoWayChatService extends Service {
  @queryManager apollo;
  @service session;
  @service apolloSubscriptions;
  @service eventPropagator;
  @service access;

  @tracked screen = '';
  @tracked selectedClient = null;
  @tracked selectedChat = null;
  @tracked selectedTab = 'all-chats';

  constructor() {
    super(...arguments);
    this.eventPropagator.subscribe(
      'clientChatChange',
      this.refetchResourcesTask.perform
    );
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.eventPropagator.unsubscribe(
      'clientChatChange',
      this.refetchResourcesTask.perform
    );
  }

  get chatMessages() {
    return this.getChatMessagesTask.lastSuccessful?.value.chatMessages?.edges.map(
      (edge) => edge.node
    );
  }

  get chatConversations() {
    return this.getChatConversationsTask.lastSuccessful?.value.chatConversations?.edges.map(
      (edge) => edge.node
    );
  }

  get isWindowChatOpen() {
    return !!this.screen;
  }

  @action
  toggleWindowChat() {
    if (this.isWindowChatOpen) {
      this.closeWindowChat();
    } else {
      this.openWindowChat();
    }
  }

  @action
  openWindowChat() {
    this.setScreen('chats-list');
  }

  @action
  closeWindowChat() {
    this.setScreen('');
    this.reset();
  }

  @action
  reset() {
    this.selectedClient = null;
    this.selectedChat = null;
    this.selectedTab = 'all-chats';
  }

  @action
  setScreen(screen) {
    this.screen = screen;
    if (this.screen === 'chats-list') {
      if (this.selectedTab === 'all-chats') {
        this.getChatConversationsTask.perform();
      } else {
        this.getMyChatConversationsTask.perform();
      }
    }
  }

  @action
  selectClient(client) {
    this.selectedClient = client;
    this.setScreen('client-chat');
    this.getChatMessagesTask.perform();
  }

  subscribe() {
    if (
      this.session.branch.isTwoWayMessagingEnabled &&
      this.access.hasPermission('two-way-chat')
    ) {
      this.apolloSubscriptions.subscribe('chatChange', {
        accountId: this.session.accountId,
      });
    }
  }

  @restartableTask
  *getChatConversationsTask(variables = {}) {
    return yield this.apollo.query({
      query: getChatConversationsQuery,
      variables,
      fetchPolicy: 'network-only',
    });
  }

  @restartableTask
  *getMyChatConversationsTask() {
    return yield this.getChatConversationsTask.perform({
      staffMemberId: this.session.user.id,
    });
  }

  @restartableTask
  *refetchResourcesTask({ resource }) {
    let clientId;
    if (resource?.clientId) {
      clientId = resource.clientId;
    }

    if (this.screen === 'chats-list') {
      if (this.selectedTab === 'all-chats') {
        yield this.getChatConversationsTask.perform();
      } else {
        yield this.getMyChatConversationsTask.perform();
      }
    }
    if (this.screen === 'client-chat' && clientId === this.selectedClient.id) {
      yield this.getChatMessagesTask.perform();
    }
  }

  @restartableTask
  *getChatMessagesTask() {
    const { id: clientId } = this.selectedClient;

    return yield this.apollo.query({
      query: getChatMessagesQuery,
      variables: {
        clientId,
      },
      fetchPolicy: 'network-only',
    });
  }

  @enqueueTask
  *sendMessageTask(message) {
    const { id: clientId, rawMobile: toMobile } = this.selectedClient;
    yield this.apollo.mutate(
      {
        mutation: sendMessageMutation,
        variables: {
          accountId: this.session.accountId,
          clientId,
          fromMobile: this.session.branch.marketing.branchSmsNumber,
          isMessageSeen: true,
          messageUuid: uuid(),
          staffFirstName: this.session.user.firstName,
          staffLastName: this.session.user.lastName,
          staffMemberId: this.session.user.id,
          text: message,
          toMobile,
        },
      },
      'sendMessage'
    );
    this.getChatMessagesTask.perform();
  }
}
